import axios from "axios";

const API_BASE_URL = process.env.REACT_APP_API_URL || "http://localhost:4000";

const publicApi = axios.create({
  baseURL: API_BASE_URL,
  withCredentials: true, // Send session cookie so the backend can recognize a logged-in owner (lets unverified owners view their own page)
});

export const getBookingPageBySlug = async (slug) => {
  try {
    const response = await publicApi.get(`/api/public/booking/${slug}`);
    return response.data;
  } catch (error) {
    if (error.response?.status === 404) {
      throw new Error("Booking page not found");
    }
    throw new Error("Failed to fetch booking page");
  }
};

export const getServicesBySlug = async (slug) => {
  try {
    const response = await publicApi.get(`/api/public/booking/${slug}/services`);
    return response.data;
  } catch (error) {
    if (error.response?.status === 404) {
      throw new Error("Services not found");
    }
    throw new Error("Failed to fetch services");
  }
};

export const calculateTravelFee = async (slug, customerAddress) => {
  try {
    const response = await publicApi.post(`/api/public/booking/${slug}/calculate-travel-fee`, {
      customerAddress
    });
    return response.data;
  } catch (error) {
    if (error.response?.status === 404) {
      throw new Error("Booking page not found");
    }
    if (error.response?.status === 400) {
      throw new Error(error.response.data?.error?.message || "Invalid request");
    }
    throw new Error("Failed to calculate travel fee");
  }
};

export const validateAddressInServiceArea = async (slug, placeId) => {
  try {
    const response = await publicApi.post(`/api/public/booking/${slug}/validate-address`, {
      placeId
    });
    return response.data;
  } catch (error) {
    if (error.response?.status === 404) {
      throw new Error("Booking page not found");
    }
    if (error.response?.status === 400) {
      throw new Error(error.response.data?.error?.message || "Invalid address");
    }
    throw new Error("Failed to validate service area");
  }
};

export const getAvailableTimeSlots = async (slug, selectedDate, availabilityIds, totalServiceDuration) => {
  try {
    const response = await publicApi.post(`/api/public/booking/${slug}/available-time-slots`, {
      selectedDate,
      availabilityIds,
      totalServiceDuration
    });
    return response.data;
  } catch (error) {
    if (error.response?.status === 404) {
      throw new Error("Booking page not found");
    }
    if (error.response?.status === 400) {
      throw new Error(error.response.data?.error?.message || "Invalid request");
    }
    throw new Error("Failed to fetch available time slots");
  }
};

export const getNextAvailableSlot = async (slug, availabilityIds, startDate = null, totalServiceDuration = null) => {
  try {
    if (!availabilityIds || !Array.isArray(availabilityIds) || availabilityIds.length === 0) {
      throw new Error("Availability IDs are required");
    }

    const response = await publicApi.post(`/api/public/booking/${slug}/next-available-slot`, {
      availabilityIds,
      startDate,
      totalServiceDuration
    });
    return response.data;
  } catch (error) {
    if (error.response?.status === 404) {
      throw new Error("Booking page not found");
    }
    if (error.response?.status === 400) {
      throw new Error(error.response.data?.error?.message || "Invalid request");
    }
    throw new Error("Failed to find next available slot");
  }
};

export const createPublicAppointment = async (slug, appointmentData) => {
  try {
    const response = await publicApi.post(`/api/public/booking/${slug}/appointments`, appointmentData);
    return response.data;
  } catch (error) {
    if (error.response?.status === 404) {
      throw new Error("Booking page not found");
    }
    if (error.response?.status === 400) {
      const err = new Error(error.response.data?.error?.message || "Invalid booking data");
      err.validationErrors = error.response.data?.error?.details;
      throw err;
    }
    if (error.response?.status === 409) {
      const err = new Error(error.response.data?.error?.message || "This time slot is no longer available");
      err.code = "SLOT_CONFLICT";
      throw err;
    }
    if (error.response?.status === 500) {
      throw new Error("Server error occurred while creating appointment");
    }
    throw new Error("Failed to create appointment");
  }
};

export const getAppointmentForManagement = async (slug, token) => {
  try {
    const response = await publicApi.get(`/api/public/booking/${slug}/appointments/manage?token=${token}`);
    return response.data;
  } catch (error) {
    if (error.response?.status === 400) {
      throw new Error(error.response.data?.error?.message || "Invalid management token");
    }
    if (error.response?.status === 403) {
      throw new Error(error.response.data?.error?.message || "Access denied");
    }
    if (error.response?.status === 404) {
      throw new Error(error.response.data?.error?.message || "Appointment or booking page not found");
    }
    throw new Error("Failed to load appointment details");
  }
};

export const cancelAppointment = async (slug, appointmentId, token, cancellationReason = "") => {
  try {
    const response = await publicApi.post(`/api/public/booking/${slug}/appointments/${appointmentId}/cancel`, {
      token,
      cancellationReason
    });
    return response.data;
  } catch (error) {
    if (error.response?.status === 400) {
      throw new Error(error.response.data?.error?.message || "Failed to cancel appointment");
    }
    if (error.response?.status === 403) {
      throw new Error(error.response.data?.error?.message || "Not authorized to cancel this appointment");
    }
    throw new Error("Failed to cancel appointment");
  }
};

export const requestReschedule = async (slug, appointmentId, token, newDate, newTime, clientTimezone, message = "", isAvailableSlot = false) => {
  try {
    const response = await publicApi.post(`/api/public/booking/${slug}/appointments/${appointmentId}/reschedule-request`, {
      token,
      newDate,
      newTime,
      clientTimezone,
      message,
      isAvailableSlot
    });
    return response.data;
  } catch (error) {
    if (error.response?.status === 400) {
      throw new Error(error.response.data?.error?.message || "Failed to submit reschedule request");
    }
    if (error.response?.status === 403) {
      throw new Error(error.response.data?.error?.message || "Not authorized to reschedule this appointment");
    }
    throw new Error("Failed to submit reschedule request");
  }
};